import { lensApi } from "@/services/lensApi";
import { getSearchParamForRequest } from "@/utils/filters";

// ── Wire types ──────────────────────────────────────────────────────
//
// Domain primitives (AlarmType, AlarmDeliveryMode, ComparisonOperator, AlarmScope,
// OwnershipType, AlarmRecipient) live in src/types/dryp/Alarm.ts and are
// reachable through the global Dryp namespace.

interface LensAlarmParameter {
	id: Dryp.ParameterId;
	name: string;
	unit: { text: string; symbol: string };
}

interface LensAlarmCounts {
	recipients: number;
	structures: number;
	triggered: number;
	warning: number;
	critical: number;
}

export interface LensAlarmResponse {
	id: Dryp.AlarmId;
	name: string;
	alarmType: Dryp.AlarmType;
	enabled: boolean;
	parameter: LensAlarmParameter;
	cooldownMinutes: number;
	criticalThreshold: number;
	criticalDeliveryMode: Dryp.AlarmDeliveryMode;
	warningThreshold: number | null;
	warningDeliveryMode: Dryp.AlarmDeliveryMode | null;
	durationMinutes: number | null;
	datapointCount: number | null;
	operator: Dryp.ComparisonOperator;
	structureScope: Dryp.AlarmScope;
	createdAt: string;
	createdByUserId: Dryp.UserId;
	counts: LensAlarmCounts;
}

export interface LensAlarmDetailsResponse extends LensAlarmResponse {
	recipients: Dryp.AlarmRecipient[];
	structureIds: Dryp.StructureId[];
}

export interface LensAlarmStateResponse {
	alarm: { id: Dryp.AlarmId; name: string };
	structure: { id: Dryp.StructureId; name: string };
	parameter: LensAlarmParameter;
	threshold: number;
	operator: Dryp.ComparisonOperator;
	isTriggered: boolean;
	severity: Dryp.AlarmSeverity;
	triggerValue: number | null;
	triggeredAt: string | null;
	lastEvaluatedAt: string;
}

interface LensStructureAlarmState {
	isTriggered: boolean;
	severity: Dryp.AlarmSeverity;
	triggerValue: number | null;
	triggeredAt: string | null;
	lastEvaluatedAt: string;
}

export interface LensStructureAlarmResponse {
	id: Dryp.AlarmId;
	name: string;
	alarmType: Dryp.AlarmType;
	enabled: boolean;
	parameter: LensAlarmParameter;
	criticalThreshold: number;
	criticalDeliveryMode: Dryp.AlarmDeliveryMode;
	warningThreshold: number | null;
	warningDeliveryMode: Dryp.AlarmDeliveryMode | null;
	durationMinutes: number | null;
	datapointCount: number | null;
	operator: Dryp.ComparisonOperator;
	structureScope: Dryp.AlarmScope;
	alarmState: LensStructureAlarmState | null;
}

interface LensStructureAlarmListResponse extends Dryp.ApiList<LensStructureAlarmResponse> {
	itemsMeta: { triggeredCount: number; notTriggeredCount: number; pendingCount: number };
}

interface StructureAlarmListFilters {
	isTriggered?: boolean;
	enabled?: boolean;
}

export interface AlarmStateListFilters {
	alarmIds?: Dryp.AlarmId[];
	structureIds?: Dryp.StructureId[];
	isTriggered?: boolean;
}

// ── Request types ───────────────────────────────────────────────────

export interface CreateAlarmRequest {
	alarmType: Dryp.AlarmType;
	name: string;
	cooldownMinutes: number;
	parameterId: Dryp.ParameterId;
	operator: Dryp.ComparisonOperator;
	criticalThreshold: number;
	criticalDeliveryMode: Dryp.AlarmDeliveryMode;
	warningThreshold?: number | null;
	warningDeliveryMode?: Dryp.AlarmDeliveryMode | null;
	durationMinutes?: number | null;
	datapointCount?: number | null;
	structureScope: Dryp.AlarmScope;
	structureIds: Dryp.StructureId[];
	recipients: Dryp.AlarmRecipient[];
}

export type UpdateAlarmRequest = Partial<CreateAlarmRequest> & { enabled?: boolean };

// ── Recipients ───────────────────────────────────────────────────────

type AlarmRecipientListResponse = Dryp.ApiList<Dryp.AlarmRecipientListItem>;

interface AlarmRecipientListFilters {
	search?: string;
	isExternal?: boolean;
	userIds?: Dryp.UserId[];
}

const getRecipientFilterParamsForRequest = (filters?: AlarmRecipientListFilters) => {
	const params: Record<string, unknown> = {
		...getSearchParamForRequest(filters?.search),
	};
	if (filters?.isExternal !== undefined) {
		params["filter[isExternal]"] = filters.isExternal;
	}
	if (filters?.userIds && filters.userIds.length > 0) {
		params["filter[userIds]"] = filters.userIds;
	}
	return params;
};

// ── API functions ───────────────────────────────────────────────────

type LensAlarmListResponse = Dryp.ApiList<LensAlarmResponse>;
type LensAlarmStateListResponse = Dryp.ApiList<LensAlarmStateResponse>;

export interface AlarmListFilters {
	search?: string;
	alarmTypes?: Dryp.AlarmType[];
	structureIds?: Dryp.StructureId[];
	enabled?: boolean;
	userIds?: string[];
}

const getAlarmFilterParamsForRequest = (filters?: AlarmListFilters) => {
	const params: Record<string, unknown> = {
		...getSearchParamForRequest(filters?.search),
	};
	if (filters?.alarmTypes && filters.alarmTypes.length > 0) {
		params["filter[types]"] = filters.alarmTypes;
	}
	if (filters?.structureIds && filters.structureIds.length > 0) {
		params["filter[structureIds]"] = filters.structureIds;
	}
	if (filters?.enabled !== undefined) {
		params["filter[enabled]"] = filters.enabled;
	}
	if (filters?.userIds && filters.userIds.length > 0) {
		params["filter[userIds]"] = filters.userIds;
	}
	return params;
};

const getAlarmStateFilterParamsForRequest = (filters?: AlarmStateListFilters) => {
	const params: Record<string, unknown> = {};
	if (filters?.alarmIds && filters.alarmIds.length > 0) {
		params["filter[alarmIds]"] = filters.alarmIds;
	}
	if (filters?.structureIds && filters.structureIds.length > 0) {
		params["filter[structureIds]"] = filters.structureIds;
	}
	if (filters?.isTriggered !== undefined) {
		params["filter[isTriggered]"] = filters.isTriggered;
	}
	return params;
};

export const alarm = {
	getAll: async (
		pagination: Dryp.PaginationQuery,
		sort?: Dryp.SortQuery,
		filters?: AlarmListFilters,
	): Promise<LensAlarmListResponse> => {
		return await lensApi.get<LensAlarmListResponse>("/alarms", {
			params: { ...pagination, ...sort, ...getAlarmFilterParamsForRequest(filters) },
		});
	},

	getById: async (id: Dryp.AlarmId): Promise<LensAlarmDetailsResponse> => {
		return await lensApi.get<LensAlarmDetailsResponse>(`/alarms/${id}`);
	},

	create: async (data: CreateAlarmRequest): Promise<LensAlarmResponse> => {
		return await lensApi.post<LensAlarmResponse>("/alarms", data);
	},

	update: async (id: number, data: UpdateAlarmRequest): Promise<void> => {
		await lensApi.put<void>(`/alarms/${id}`, data);
	},

	delete: async (id: number): Promise<void> => {
		await lensApi.delete<void>(`/alarms/${id}`);
	},

	acknowledge: async (id: number): Promise<void> => {
		await lensApi.post<void>(`/alarms/${id}/acknowledge`, {});
	},

	getForStructure: async (
		structureId: Dryp.StructureId,
		pagination: Dryp.PaginationQuery,
		sort?: Dryp.SortQuery,
		filters?: StructureAlarmListFilters,
	): Promise<LensStructureAlarmListResponse> => {
		const params: Record<string, unknown> = { ...pagination, ...sort };
		if (filters?.isTriggered !== undefined) params["filter[isTriggered]"] = filters.isTriggered;
		if (filters?.enabled !== undefined) params["filter[enabled]"] = filters.enabled;
		return await lensApi.get<LensStructureAlarmListResponse>(`/structures/${structureId}/alarms`, { params });
	},

	getAlarmStates: async (
		pagination: Dryp.PaginationQuery,
		sort?: Dryp.SortQuery,
		filters?: AlarmStateListFilters,
	): Promise<LensAlarmStateListResponse> => {
		const sortByLatestTriggeredAt: Dryp.SortQuery = sort ?? { sort: "triggeredAt", direction: "desc" };
		return await lensApi.get<LensAlarmStateListResponse>("/alarm-states", {
			params: { ...pagination, ...sortByLatestTriggeredAt, ...getAlarmStateFilterParamsForRequest(filters) },
		});
	},

	getRecipients: async (
		alarmId: Dryp.AlarmId,
		pagination: Dryp.PaginationQuery,
		sort?: Dryp.SortQuery,
		filters?: AlarmRecipientListFilters,
	): Promise<AlarmRecipientListResponse> => {
		return await lensApi.get<AlarmRecipientListResponse>(`/alarms/${alarmId}/recipients`, {
			params: { ...pagination, ...sort, ...getRecipientFilterParamsForRequest(filters) },
		});
	},

	// recipientId is either the GUID (internal) or numeric string (external) returned in any recipient response.
	// For self-unsubscribe pass the current user's userId — backend's :guid route resolves it the same way.
	removeRecipient: async (alarmId: Dryp.AlarmId, recipientId: Dryp.AlarmRecipientId | Dryp.UserId): Promise<void> => {
		await lensApi.delete<void>(`/alarms/${alarmId}/recipients/${recipientId}`);
	},
};
